import path from 'path'

import { TemplateNotFoundError } from '../../errors'
import type { Answers, Template } from '../../templates/manifest'
import { DEFAULT_TEMPLATE_ALIAS, findTemplate, listTemplates } from '../../templates/registry'
import { scaffoldService } from '../../templates/scaffold'
import type { OperationOptions } from '../types'
import { emitter } from '../types'
import type { Ask } from './answers'
import { resolveAnswers } from './answers'

// Scaffolding one service into an existing project.

export type CreateServiceEvent =
  | { type: 'template'; template: Template }
  | { type: 'created-services-directory' }
  | { type: 'created'; relativePath: string; files: string[] }

export interface CreateServiceOptions extends OperationOptions<CreateServiceEvent> {
  /** Answers the template's prompts, keyed by prompt name. `id` and `name` are expected by every template. */
  answers?: Answers
  /** Take every default rather than asking. */
  acceptDefaults?: boolean
  /** How to ask for anything still unanswered. Omitted means an unanswered prompt throws. */
  ask?: Ask
}

export interface ScaffoldedService {
  serviceId: string
  serviceDirectory: string
  /** `serviceDirectory` relative to the project root, for printing. */
  relativePath: string
  files: string[]
}

/** Resolves a template id — the `demo` alias included — or throws naming what is available. */
export function resolveTemplateId(templateId: string, templates: Template[] = listTemplates()): Template {
  const template = findTemplate(templateId)

  if (!template) {
    throw new TemplateNotFoundError(
      templateId,
      [...templates.map(entry => entry.manifest.id), DEFAULT_TEMPLATE_ALIAS]
    )
  }

  return template
}

/**
 * Ask the template's questions and write the service — the part `createService` and `init` share.
 *
 * Reports the files it created; the caller owns the "next steps" it prints afterwards, since those differ
 * between scaffolding into an existing project and bootstrapping a new one.
 */
export async function scaffoldFromTemplate(
  template: Template,
  options: CreateServiceOptions = {},
  projectRoot: string = options.cwd ?? process.cwd()
): Promise<ScaffoldedService> {
  const emit = emitter(options)

  emit({ type: 'template', template })

  const answers = await resolveAnswers(template.manifest.prompts, {
    prefilled     : options.answers,
    constants     : template.manifest.values,
    acceptDefaults: options.acceptDefaults,
    ask           : options.ask,
    projectRoot,
  })

  // Every template is expected to collect the service id under this name — it is what the folder is
  // called and what `createExtension({ id })` freezes.
  const serviceId = String(answers.id)

  const { serviceDirectory, files, createdServicesDirectory } =
    scaffoldService(template, answers, serviceId, projectRoot)

  const relativePath = path.relative(projectRoot, serviceDirectory) || serviceDirectory
  const written = files.map(file => file.relativePath)

  if (createdServicesDirectory) {
    emit({ type: 'created-services-directory' })
  }

  emit({ type: 'created', relativePath, files: written })

  return { serviceId, serviceDirectory, relativePath, files: written }
}

/** Scaffold a service from the named template into the project. */
export async function createService(
  templateId: string,
  options: CreateServiceOptions = {}
): Promise<ScaffoldedService> {
  return scaffoldFromTemplate(resolveTemplateId(templateId), options, options.cwd ?? process.cwd())
}
